import {
  Box,
  Divider,
  Stack,
  Tooltip,
  Typography,
  darken,
  useTheme,
} from "@mui/material"
import { pathOr, stringToPath } from "remeda"
import type { RequireAllOrNone } from "type-fest"
import { NumberFormat } from "../../../dataDisplay"
import { Progress, type ProgressProps } from "../../Progress"

export type ProgressBarProps = {
  progress: number
  currentValue: number
  goal: number
  showHalfMark?: boolean
  unit: string
} & RequireAllOrNone<{ referenceValue: number }, "referenceValue"> &
  Pick<ProgressProps, "endLabelPosition" | "showEndMark" | "color">

type ProgressBarTooltip = Pick<
  ProgressBarProps,
  "progress" | "currentValue" | "goal" | "referenceValue" | "unit" | "color"
>

const ProgressTooltip = ({
  currentValue,
  goal,
  unit,
  progress,
  referenceValue,
  color = "primary.main",
}: ProgressBarTooltip) => {
  const { palette } = useTheme()
  const finalColor = pathOr(palette, stringToPath(color), color) as string
  const darkColor = darken(finalColor, 0.3)

  return (
    <Stack gap={0.5}>
      <Typography fontSize={(theme) => theme.typography.pxToRem(12)}>
        Progression:
      </Typography>
      <Stack>
        <Typography
          fontSize={(theme) => theme.typography.pxToRem(12)}
          color={finalColor}
        >
          <b>{currentValue}</b> / {goal} {unit}
        </Typography>
        <Typography
          fontSize={(theme) => theme.typography.pxToRem(14)}
          fontWeight="bold"
          color={darkColor}
        >
          {progress.toFixed(2)} %
        </Typography>
      </Stack>
      {!!referenceValue && (
        <>
          <Divider />
          <Typography
            fontSize={(theme) => theme.typography.pxToRem(12)}
            color="grey.light"
          >
            Réalisé n-1:{" "}
            <b>
              {referenceValue} {unit}
            </b>
          </Typography>
        </>
      )}
    </Stack>
  )
}

export const ProgressBar = ({
  showHalfMark = false,
  showEndMark = true,
  endLabelPosition,
  ...progressBarProps
}: ProgressBarProps) => {
  const { palette } = useTheme()
  const { goal, progress, referenceValue, unit, color } = progressBarProps
  const lastYearProgress = referenceValue
    ? Math.min((100 * referenceValue) / goal, 100)
    : 0
  const extraProgressProps = referenceValue
    ? {
        referenceValue: lastYearProgress,
        referenceLabel: <NumberFormat value={referenceValue} suffix={` ${unit}`} />,
      }
    : {}

  return (
    <Tooltip
      title={<ProgressTooltip {...progressBarProps} />}
      followCursor
      arrow={false}
    >
      <Box>
        <Progress
          value={progress}
          color={color}
          showPercentage
          showEndMark={showEndMark}
          endLabelPosition={endLabelPosition}
          endLabel={<NumberFormat value={goal} suffix={` ${unit}`} />}
          {...(showHalfMark && { halfMarkColor: palette.common.americanDiamond })}
          {...extraProgressProps}
          sx={{ py: 0 }}
        />
      </Box>
    </Tooltip>
  )
}
